/**
 * Sessioni di assistenza: dal Core un platform admin entra in un tenant cliente come se stesso,
 * con i permessi di amministratore del tenant e per un tempo limitato (migrazione
 * sessioni_assistenza, funzione tenant-access).
 *
 * - startSupportAccess: apre la sessione e una nuova scheda sul dominio del prodotto del tenant,
 *   con un accesso monouso nel frammento (#assistenza=…, mai inviato ai server);
 * - SupportAccessGate: su quella scheda scambia l'accesso con la sessione e porta alla home;
 * - SupportSessionBanner: barra «Sessione di assistenza» con scadenza e tasto Termina.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Headset, Loader2, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useModuleSession } from "@/contexts/ModuleSessionContext";
import { PRODUCTS, isProductKey, productForPath, type ProductKey } from "@/platform/products";
import { platform } from "@/platform/runtime";
import { AuthNotice, AuthShell } from "@/platform/auth/AuthShell";

const HASH_PREFIX = "#assistenza=";
/** Segna, per questa scheda, che la sessione è di assistenza */
const TAB_FLAG = "ml_support_session";
/** Pagina del Core da cui si è entrati: Termina riporta lì */
const RETURN_KEY = "ml_support_return";

/** Solo percorsi interni: niente redirect verso siti esterni */
function safeCorePath(path: string | null): string {
  return path && path.startsWith("/") && !path.startsWith("//") ? path : "/core/admin/tenants";
}

function parseHash(): { token: string; ritorno: string } | null {
  if (!window.location.hash.startsWith(HASH_PREFIX)) return null;
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get("assistenza");
  return token ? { token, ritorno: safeCorePath(params.get("ritorno")) } : null;
}

const PRODUCT_HOME: Record<ProductKey, string> = {
  console: "/core/admin",
  construction: "/construction",
  supply: "/supply",
  crm: "/crm/contacts",
  billing: "/billing/invoices",
};

type SupportRpc = (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
const rpc = supabase.rpc.bind(supabase) as unknown as SupportRpc;

/** True se l'utente corrente (platform admin) ha una sessione di assistenza attiva. */
export async function hasActiveSupportSession(): Promise<boolean> {
  const { data } = await rpc("support_session_current");
  return Array.isArray(data) && data.length > 0;
}

/**
 * Dal Core: apre la sessione di assistenza nel tenant e porta, nella stessa scheda, all'app del
 * tenant. Termina riporterà alla pagina del Core da cui si è partiti.
 */
export async function startSupportAccess(tenantId: string): Promise<{ tenantName: string; product: ProductKey }> {
  const { data, error } = await supabase.functions.invoke("tenant-access", { body: { tenant_id: tenantId } });
  if (error) {
    const body = await (error as { context?: Response }).context?.json?.().catch(() => null);
    throw new Error(body?.error ?? "Impossibile aprire la sessione di assistenza");
  }
  const product: ProductKey = isProductKey(data?.product) ? data.product : "construction";
  const hash = new URLSearchParams({
    assistenza: data.token_hash,
    ritorno: window.location.pathname + window.location.search,
  });
  const path = `${PRODUCTS[product].loginPath}#${hash.toString()}`;
  window.location.assign(new URL(platform.urlFor(product, path), window.location.origin).href);
  return { tenantName: data.tenant_name, product };
}

function useClearProductSession() {
  const { clearConstructionSession, clearBillingSession, clearCrmSession } = useModuleSession();
  return useCallback(
    (product: ProductKey | null) => {
      if (product === "construction") clearConstructionSession();
      else if (product === "billing") clearBillingSession();
      else if (product === "crm") clearCrmSession();
    },
    [clearBillingSession, clearConstructionSession, clearCrmSession],
  );
}

/** Sulla scheda aperta dal Core: accesso monouso → sessione → home del prodotto. */
export function SupportAccessGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { setConstructionSession, setBillingSession, setCrmSession } = useModuleSession();
  const [entry] = useState(parseHash);
  const tokenHash = entry?.token ?? null;
  const [status, setStatus] = useState<"idle" | "pending" | "error">(tokenHash ? "pending" : "idle");
  const [error, setError] = useState<string | null>(null);
  const product = productForPath(window.location.pathname) ?? platform.product ?? "construction";

  useEffect(() => {
    if (!tokenHash) return;
    // Il token non resta nella barra degli indirizzi né nella cronologia
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    let cancelled = false;
    (async () => {
      try {
        const { data, error: otpError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "magiclink" });
        if (otpError || !data.user) throw new Error("Link di accesso scaduto o già usato. Riaprilo dal Core.");
        const { data: tenantId } = await supabase.rpc("get_user_tenant", { _user_id: data.user.id });
        const email = data.user.email ?? "";
        if (product === "construction") setConstructionSession(email, data.user.id, tenantId as string);
        else if (product === "billing") setBillingSession(email);
        else if (product === "crm") setCrmSession(email, data.user.id, tenantId as string);
        try {
          sessionStorage.setItem(TAB_FLAG, "1");
          sessionStorage.setItem(RETURN_KEY, entry?.ritorno ?? "");
        } catch {
          /* il banner resta nascosto, la sessione funziona comunque */
        }
        if (cancelled) return;
        setStatus("idle");
        navigate(PRODUCT_HOME[product], { replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Accesso non riuscito");
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenHash]);

  if (status === "idle") return <>{children}</>;
  return (
    <AuthShell
      product={product}
      title="Sessione di assistenza"
      subtitle={status === "pending" ? "Accesso al tenant in corso…" : "Non è stato possibile entrare."}
    >
      {status === "pending" ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <AuthNotice>{error}</AuthNotice>
      )}
    </AuthShell>
  );
}

function readTabFlag() {
  try {
    return sessionStorage.getItem(TAB_FLAG) === "1";
  } catch {
    return false;
  }
}

interface CurrentSession {
  tenant_name: string;
  expires_at: string;
}

/** Barra visibile per tutta la sessione di assistenza, solo nella scheda aperta dal Core. */
export function SupportSessionBanner() {
  const location = useLocation();
  const clearProductSession = useClearProductSession();
  const [active, setActive] = useState(readTabFlag);
  const [current, setCurrent] = useState<CurrentSession | null>(null);

  // Il segno arriva dopo l'ingresso (SupportAccessGate): si rilegge a ogni cambio di pagina
  useEffect(() => {
    setActive(readTabFlag());
  }, [location.pathname]);
  const [ending, setEnding] = useState(false);
  const product = productForPath(location.pathname) ?? platform.product;

  const leave = useCallback(
    async (endOnServer: boolean) => {
      setEnding(true);
      // Le chiamate PostgREST non hanno .catch: si usa try
      try {
        if (endOnServer) await rpc("support_session_end");
        await supabase.auth.signOut({ scope: "local" });
      } catch {
        /* si esce comunque; la sessione scade da sola */
      }
      clearProductSession(product);
      let ritorno: string | null = null;
      try {
        ritorno = sessionStorage.getItem(RETURN_KEY);
        sessionStorage.removeItem(TAB_FLAG);
        sessionStorage.removeItem(RETURN_KEY);
      } catch {
        /* niente */
      }
      setActive(false);
      // Si torna alla pagina del Core da cui si era entrati
      window.location.replace(new URL(platform.urlFor("console", safeCorePath(ritorno)), window.location.origin).href);
    },
    [clearProductSession, product],
  );

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const check = async () => {
      const { data } = await rpc("support_session_current");
      if (cancelled) return;
      const row = Array.isArray(data) ? (data[0] as CurrentSession | undefined) : undefined;
      if (row) setCurrent(row);
      else void leave(false); // scaduta o chiusa altrove
    };
    void check();
    const timer = window.setInterval(check, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [active, leave]);

  if (!active || !current) return null;
  const until = new Date(current.expires_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+5rem)] z-[60] flex justify-center px-4 md:bottom-4">
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-amber-300 bg-amber-50/95 py-1.5 pl-3 pr-1.5 text-sm text-amber-900 shadow-lg backdrop-blur">
        <Headset className="h-4 w-4 shrink-0" />
        <span className="truncate">
          Assistenza in <strong>{current.tenant_name}</strong>
          <span className="hidden sm:inline"> · fino alle {until}</span>
        </span>
        <button
          type="button"
          onClick={() => void leave(true)}
          disabled={ending}
          className="flex items-center gap-1.5 rounded-full bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60"
        >
          {ending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
          Termina
        </button>
      </div>
    </div>
  );
}
